/**
 * Administration > Users (M2 W5b) — the primary admin screen. Lists platform operators
 * (email, name, status, roles) in a SkyDataGrid with name/email search + a status filter,
 * creates a user (email/name/password, password is a MASKED input that is never echoed back
 * or logged), activates/deactivates a user (PATCH status), and grants/revokes role
 * assignments at a chosen scope (Global/Tenant/Instance). Mutations REFETCH (no optimistic
 * client state). The API never returns a password hash, so none is ever held or rendered.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, UserPlus, ShieldCheck, Trash2 } from "lucide-react";
import {
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyBadge,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { ApiProblem } from "../../lib/api";
import {
  listUsers,
  getUser,
  createUser,
  updateUser,
  grantUserRole,
  revokeUserRole,
  listRoles,
  listTenants,
  listInstances,
  type AdminUser,
  type UserRoleAssignment,
  type AdminRole,
  type AdminTenant,
  type AdminInstance,
} from "../../lib/adminApi";
import { usePermission } from "../../lib/session";
import { AdminPage, AdminError, StatusBadge, scopeLabel } from "./adminShared";

const STATUS_OPTIONS = [
  { value: "all", text: "All statuses" },
  { value: "active", text: "Active" },
  { value: "pending", text: "Pending" },
  { value: "blocked", text: "Blocked" },
];

function problemTitle(e: unknown, fallback: string): string {
  return e instanceof ApiProblem ? e.problem.title : fallback;
}

export function AdminUsers() {
  const { canManage } = usePermission();
  const canEdit = canManage("Users");

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roleMap, setRoleMap] = useState<Record<string, UserRoleAssignment[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-row busy set for the activate/deactivate action (disables ONLY that row's button
  // while its PATCH + refetch is in flight). A busy row must never gate a DIFFERENT row's
  // button, so this is a set of ids, not a single global id. The ref is a synchronous
  // re-entrancy guard so a rapid double-click on the SAME row cannot fire two PATCHes
  // before the disabled state re-renders.
  const [busyRows, setBusyRows] = useState<Set<string>>(() => new Set());
  const busyRowsRef = useRef<Set<string>>(new Set());

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Generation counter: the list load fans out N parallel getUser() detail calls, so a
  // post-mutation reload can resolve out of order and let a stale roleMap win. Stamp each
  // load and gate every setState on it being the latest, so only the newest reload applies.
  const loadGenRef = useRef(0);

  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    return listUsers()
      .then(async ({ data }) => {
        if (gen === loadGenRef.current) setUsers(data);
        // The list endpoint omits role assignments; fetch each user's detail to populate the
        // roles column. The operator set is small, so a parallel fan-out is acceptable here.
        const entries = await Promise.all(
          data.map(async (u) => {
            try {
              const { data: detail } = await getUser(u.id);
              return [u.id, detail.roles] as const;
            } catch {
              return [u.id, []] as const;
            }
          }),
        );
        // A slower earlier load must not clobber a newer one's roleMap.
        if (gen !== loadGenRef.current) return;
        setRoleMap(Object.fromEntries(entries));
      })
      .catch((e: unknown) => {
        if (gen !== loadGenRef.current) return;
        setError(problemTitle(e, "Failed to load users."));
        setUsers([]);
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) || (u.fullName ?? "").toLowerCase().includes(q)
      );
    });
  }, [users, search, statusFilter]);

  // ── create-user dialog ──────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);

  // ── manage-roles dialog ─────────────────────────────────────────────────────
  const [rolesUser, setRolesUser] = useState<AdminUser | null>(null);

  async function toggleActive(u: AdminUser) {
    // Synchronous re-entrancy guard scoped to THIS row: block a second click on the same row
    // before its disabled state re-renders, while leaving every other row clickable.
    if (busyRowsRef.current.has(u.id)) return;
    busyRowsRef.current.add(u.id);
    setBusyRows(new Set(busyRowsRef.current));
    const next = u.status === "active" ? "blocked" : "active";
    try {
      await updateUser(u.id, { status: next });
      await load();
    } catch (e: unknown) {
      setError(problemTitle(e, "Failed to update user."));
    } finally {
      busyRowsRef.current.delete(u.id);
      setBusyRows(new Set(busyRowsRef.current));
    }
  }

  const columns: SkyDataGridColumn<AdminUser>[] = [
    { key: "email", header: "Email", width: "24%" },
    {
      key: "fullName",
      header: "Display name",
      width: "18%",
      renderCell: (_v, row) => row.fullName ?? <SkyText size={200}>Not set</SkyText>,
    },
    {
      key: "status",
      header: "Status",
      width: "12%",
      renderCell: (_v, row) => <StatusBadge status={row.status} />,
    },
    {
      key: "roles",
      header: "Roles",
      width: "26%",
      renderCell: (_v, row) => {
        const assigns = roleMap[row.id] ?? [];
        if (assigns.length === 0) return <SkyText size={200}>No roles</SkyText>;
        return (
          <div className="flex flex-wrap gap-1">
            {assigns.map((a) => (
              <SkyBadge key={a.id} appearance="tint" color="informative">
                {a.roleLabel} ({scopeLabel(a.tenantId, a.instanceId).toLowerCase()})
              </SkyBadge>
            ))}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      width: "20%",
      renderCell: (_v, row) => (
        <div className="flex gap-2">
          <SkyButton
            appearance="subtle"
            size="small"
            icon={ShieldCheck}
            disabled={!canEdit}
            onClick={() => setRolesUser(row)}
          >
            Roles
          </SkyButton>
          <SkyButton
            appearance="outline"
            size="small"
            disabled={!canEdit || busyRows.has(row.id)}
            onClick={() => toggleActive(row)}
          >
            {row.status === "active" ? "Deactivate" : "Activate"}
          </SkyButton>
        </div>
      ),
    },
  ];

  return (
    <AdminPage
      crumbs={[
        { key: "home", label: "skyCare", to: "/" },
        { key: "admin", label: "Administration" },
        { key: "users", label: "Users" },
      ]}
      title="Users"
      description="Platform operators and their role assignments. Passwords are write-only and never displayed."
      requires={["viewUsers", "manageUsers"]}
      headerAction={
        canEdit ? (
          <SkyButton appearance="primary" icon={UserPlus} onClick={() => setCreateOpen(true)}>
            New user
          </SkyButton>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72 max-w-full">
          <SkyInput
            label="Search"
            placeholder="Email or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            clearable
            onClear={() => setSearch("")}
            contentBefore={<Search size={16} aria-hidden />}
          />
        </div>
        <div className="w-56 max-w-full">
          <SkyDropdown
            label="Status"
            options={STATUS_OPTIONS}
            selectedOptions={[statusFilter]}
            onOptionSelect={(vals) => setStatusFilter(vals[0] ?? "all")}
          />
        </div>
      </div>

      {error && <AdminError title="Could not complete the request" message={error} />}

      <SkyDataGrid<AdminUser>
        data={filtered}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        pagination
        pageSize={10}
        emptyMessage={search || statusFilter !== "all" ? "No users match the filter" : "No users found"}
      />

      {createOpen && (
        <CreateUserDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}

      {rolesUser && (
        <ManageRolesDialog
          user={rolesUser}
          assignments={roleMap[rolesUser.id] ?? []}
          onClose={() => setRolesUser(null)}
          onChanged={load}
        />
      )}
    </AdminPage>
  );
}

// ── Create-user dialog ─────────────────────────────────────────────────────────
function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = /\S+@\S+\.\S+/.test(email) && password.length >= 8;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await createUser({
        email: email.trim(),
        fullName: fullName.trim() || null,
        password,
      });
      onCreated();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to create user."));
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Create user"
      description="The new operator can sign in once a role is granted. The password is stored hashed and never shown again."
      size="md"
      primaryAction={{ label: "Create user", onClick: submit, loading: busy, disabled: !valid || busy }}
      secondaryAction={{ label: "Cancel", onClick: onClose, disabled: busy }}
    >
      <div className="flex flex-col gap-3">
        {err && <AdminError title="Could not create user" message={err} />}
        <SkyInput
          label="Email"
          type="email"
          required
          placeholder="operator@org.example"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <SkyInput
          label="Display name"
          placeholder="Full name (optional)"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <SkyInput
          label="Password"
          type="password"
          required
          placeholder="At least 8 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Minimum 8 characters. Stored hashed; it cannot be retrieved later."
        />
      </div>
    </SkyDialog>
  );
}

// ── Manage-roles dialog ──────────────────────────────────────────────────────
function ManageRolesDialog({
  user,
  assignments,
  onClose,
  onChanged,
}: {
  user: AdminUser;
  assignments: UserRoleAssignment[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [instances, setInstances] = useState<AdminInstance[]>([]);
  const [roleId, setRoleId] = useState<string>("");
  const [scope, setScope] = useState<"global" | "tenant" | "instance">("global");
  const [scopeId, setScopeId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load role catalog (required) + tenants/instances for scope (best-effort; an admin holds
  // the manage* perms, so these normally succeed — degrade to Global-only if they 403).
  // A `cancelled` flag scopes all three in-flight fetches to this open effect: if the dialog
  // closes (or unmounts) before they settle, their responses are dropped so a reopen can never
  // render a stale role/tenant/instance catalog raced in from the previous open.
  useEffect(() => {
    let cancelled = false;
    listRoles()
      .then(({ data }) => {
        if (!cancelled) setRoles(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(problemTitle(e, "Failed to load roles."));
      });
    listTenants()
      .then(({ data }) => {
        if (!cancelled) setTenants(data);
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    listInstances()
      .then(({ data }) => {
        if (!cancelled) setInstances(data);
      })
      .catch(() => {
        if (!cancelled) setInstances([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const scopeOptions = [
    { value: "global", text: "Global (all tenants)" },
    { value: "tenant", text: "Tenant-scoped" },
    { value: "instance", text: "Instance-scoped" },
  ];

  const canGrant =
    roleId !== "" && (scope === "global" || scopeId !== "");

  async function grant() {
    setBusy(true);
    setErr(null);
    try {
      await grantUserRole(user.id, {
        roleId,
        tenantId: scope === "tenant" ? scopeId : null,
        instanceId: scope === "instance" ? scopeId : null,
      });
      setRoleId("");
      setScope("global");
      setScopeId("");
      onChanged();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to grant role."));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(assignmentId: string) {
    setBusy(true);
    setErr(null);
    try {
      await revokeUserRole(user.id, assignmentId);
      onChanged();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to revoke role."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Roles for ${user.email}`}
      description="Grant a role at a scope, or revoke an existing assignment. Changes are audited."
      size="lg"
      primaryAction={{ label: "Done", onClick: onClose }}
    >
      <div className="flex flex-col gap-4">
        {err && <AdminError title="Role change failed" message={err} />}

        <div>
          <SkyText size={300} weight="semibold">
            Current assignments
          </SkyText>
          {assignments.length === 0 ? (
            <div className="mt-1">
              <SkyText size={200}>No roles assigned yet.</SkyText>
            </div>
          ) : (
            <div className="mt-2 flex flex-col gap-2">
              {assignments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <SkyBadge appearance="tint" color="informative">
                      {a.roleLabel}
                    </SkyBadge>
                    <SkyText size={200}>{scopeLabel(a.tenantId, a.instanceId)} scope</SkyText>
                  </div>
                  <SkyButton
                    appearance="subtle"
                    size="small"
                    icon={Trash2}
                    disabled={busy}
                    onClick={() => revoke(a.id)}
                  >
                    Revoke
                  </SkyButton>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 pt-3">
          <SkyText size={300} weight="semibold">
            Grant a role
          </SkyText>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <div className="w-56 max-w-full">
              <SkyDropdown
                label="Role"
                placeholder="Select a role"
                options={roles.map((r) => ({ value: r.id, text: r.label }))}
                selectedOptions={roleId ? [roleId] : []}
                onOptionSelect={(vals) => setRoleId(vals[0] ?? "")}
              />
            </div>
            <div className="w-56 max-w-full">
              <SkyDropdown
                label="Scope"
                options={scopeOptions}
                selectedOptions={[scope]}
                onOptionSelect={(vals) => {
                  setScope((vals[0] as "global" | "tenant" | "instance") ?? "global");
                  setScopeId("");
                }}
              />
            </div>
            {scope === "tenant" && (
              <div className="w-56 max-w-full">
                <SkyDropdown
                  label="Tenant"
                  placeholder="Select a tenant"
                  options={tenants.map((t) => ({ value: t.id, text: t.displayName }))}
                  selectedOptions={scopeId ? [scopeId] : []}
                  onOptionSelect={(vals) => setScopeId(vals[0] ?? "")}
                />
              </div>
            )}
            {scope === "instance" && (
              <div className="w-56 max-w-full">
                <SkyDropdown
                  label="Instance"
                  placeholder="Select an instance"
                  options={instances.map((i) => ({ value: i.id, text: i.label }))}
                  selectedOptions={scopeId ? [scopeId] : []}
                  onOptionSelect={(vals) => setScopeId(vals[0] ?? "")}
                />
              </div>
            )}
            <SkyButton appearance="primary" disabled={!canGrant || busy} onClick={grant}>
              Grant role
            </SkyButton>
          </div>
        </div>
      </div>
    </SkyDialog>
  );
}
